import styled from 'styled-components';
import { FontFamily } from 'styled/fonts/font-family';
import { mqMediumMin, mqLargeMin } from 'styled/tools/media-queries';

export const Section = styled.section`
  background: var(--fill-primary);
  padding-block: var(--spacing-64);

  @media ${mqMediumMin} {
    padding-block: var(--spacing-72);
  }

  @media ${mqLargeMin} {
    padding-block: 133px;
  }
`;

export const CarouselContainer = styled.div`
  display: grid;

  .swiper {
    padding-inline: var(--spacing-16);
    max-width: 100%;

    @media ${mqMediumMin} {
      padding-inline: var(--spacing-24);
    }

    @media ${mqLargeMin} {
      padding-inline: var(--spacing-32);
    }
  }

  .swiper-wrapper {
    padding-block-end: var(--spacing-8);
    width: 100%;
  }

  .swiper:not(.swiper-backface-hidden) .swiper-slide {
    transform: none;
    transition: none;
    opacity: 0;
  }

  .swiper-slide:not(.swiper-slide-prev, .swiper-slide-next) {
    transform: scale(0.95);
  }

  .swiper-backface-hidden .swiper-slide {
    transform: scale(0.95) translateZ(0);
    opacity: 1;
    transition: transform 0.3s ease-in-out 0.1s;
  }

  .swiper-backface-hidden .swiper-slide-prev {
    transform: translateX(-40px) translateZ(0) scale(0.95);
  }

  .swiper-backface-hidden .swiper-slide-next {
    transform: translateX(40px) translateZ(0) scale(0.95);
  }

  .swiper-slide.swiper-slide-active {
    transform: scale(1) !important;
  }

  &.in-view .swiper-backface-hidden .swiper-slide.swiper-slide-prev,
  &.in-view .swiper-backface-hidden .swiper-slide.swiper-slide-next {
    transform: scale(0.95) translateZ(0) !important;
  }

  .swiper-backface-hidden .swiper-slide.swiper-slide-active {
    transform: scale(1) translateZ(0) !important;
  }
`;

export const Pagination = styled.div`
  filter: url(#filter);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-inline: auto;
  width: fit-content;
`;

export const Thumbnails = styled.div`
  background: #efefea;
  border-radius: var(--radius-full);
  padding: 12px;
  display: flex;
  align-items: center;
  gap: var(--spacing-16);
  margin-left: -12px;

  & > div {
    cursor: pointer;
  }
`;

export const Frame = styled.div<{ type?: string }>`
  --ratio: 5 / 6;
  aspect-ratio: var(--ratio);
  align-items: center;
  border-radius: var(--radius-md);
  display: flex;
  position: relative;
  overflow: hidden;

  @media ${mqLargeMin} {
    --ratio: 16 / 9;
  }

  ${({ type }) =>
    type === 'rounded' &&
    `
          border-radius: 100%;
          max-width: 36px;
      `}

  & > img,
    & > picture,
    & > video {
    block-size: 100%;
    inline-size: 100%;
    object-fit: cover;
  }
`;

export const Controls = styled.div`
  background: #efefea;
  border-radius: 50%;
  padding: 12px;
  align-items: center;
  gap: var(--spacing-16);
  display: flex;
`;

export const ProgressWrapper = styled.div`
  border-radius: var(--radius-full);
  height: 36px;
  width: fit-content;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

export const ProgressBuffer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export const ProgressBar = styled.div`
  background-color: rgba(0, 0, 0, 0.1);
  height: 100%;
  width: 100%;
  inset: 0;
  transform-origin: left;
  position: absolute;
  pointer-events: none;
  transform: scaleX(0);
  z-index: 3;
`;

export const ProgressButton = styled.button`
  --focus-color: var(--fill-brand);
  --focus-offset: -1ch;
  color: var(--fill-brand);
  font-family: ${FontFamily.sans};
  font-size: var(--font-size-350);
  font-weight: 350;
  cursor: pointer;
  appearance: none;
  background-color: var(--fill-primary);
  border: none;
  display: flex;
  gap: var(--spacing-8);
  align-items: center;
  padding: 10px;
  overflow: hidden;
  z-index: 2;
`;

export const FullscreenButton = styled.button`
  --focus-color: var(--fill-primary);
  position: absolute;
  background: var(--color-neutral-frosted-glass-35, rgba(73, 73, 73, 0.7));
  color: var(--fill-primary);
  top: var(--spacing-16);
  right: var(--spacing-16);
  border: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: var(--radius-full);
  transform: scale(0);
  transition: transform 0.2s;
  transition-delay: 0.3s;
  z-index: 10;

  > svg > path {
    color: var(--fill-primary);
    transition: 0.2s;
  }

  &:hover {
    > .fullscreen {
      :nth-child(1) {
        transform: translate(-3px, -3px);
      }
      :nth-child(2) {
        transform: translate(-3px, 3px);
      }
      :nth-child(3) {
        transform: translate(3px, -3px);
      }
      :nth-child(4) {
        transform: translate(3px, 3px);
      }
    }
  }

  &.is-active {
    transform: none;
  }
`;

export const transitions = {
  fade: 'opacity 0.5s cubic-bezier(0.75, 0, 0.25, 1) 0.3s',
};

export const Review = styled.div`
  color: var(--fill-primary);
  font-family: ${FontFamily.sans};
  font-size: var(--font-size-600);
  font-weight: 350;
  text-align: left;
  position: absolute;
  bottom: var(--spacing-24);
  left: var(--spacing-24);
  max-width: 300px;
  width: 100%;
  opacity: 0;
  transition: ${transitions.fade};
  &.is-active {
    opacity: 1;
  }
`;

export const Member = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-inline: var(--spacing-24);
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate3D(0, -50%, 0);
  width: 100%;
  opacity: 0;
  transition: ${transitions.fade};
  &.is-active {
    opacity: 1;
  }

  @media ${mqLargeMin} {
    padding-inline: 57px;
  }
`;

export const MemberPill = styled.span`
  color: var(--fill-primary);
  font-family: ${FontFamily.sansMedium};
  font-size: var(--font-size-350);
  font-weight: 500;
  line-height: 110%;
  letter-spacing: 0.56px;
  text-transform: uppercase;
  border-radius: var(--radius-full);
  border: 1.5px solid currentColor;
  padding-block: var(--spacing-4);
  padding-inline: var(--spacing-8);
  vertical-align: middle;
  display: inline-block;
`;
